import type { WorkbookFileOutput as FileOutput } from '@erp/shared-types';
import { rpcClient } from '@/api/rpc/client';
import { startDownloadInFrame } from '@/api/rpc/downloadUrl';
import type { WorkbookEditor } from './useWorkbookEditor';
import type {
  WorkbookFileFormat,
  WorkbookFileLink,
} from './WorkbookFilePreview';

/**
 * Saves, then generates a File Output and links to it: its tab names and
 * number formats come from the saved Definition. Download and Preview both
 * read this link, so a preview shows exactly what a download saves.
 */
export async function workbookOutputLink(
  editor: Pick<WorkbookEditor, 'save' | 'workbookId'>,
  output: Pick<FileOutput, 'id' | 'fileName'>,
  format: WorkbookFileFormat,
  /** Download although a tab's Result failed an error-severity Validation. */
  acceptFailedValidation = false,
): Promise<WorkbookFileLink> {
  await editor.save();
  const { url } = await rpcClient.workbooks.download({
    workbookId: editor.workbookId,
    outputId: output.id,
    format,
    acceptFailedValidation,
  });
  return {
    url,
    fileName:
      format === 'csv'
        ? output.fileName.replace(/\.xlsx$/i, '.csv')
        : output.fileName,
  };
}

/** Saves, then downloads a File Output. */
export async function downloadWorkbookOutput(
  editor: Pick<WorkbookEditor, 'save' | 'workbookId'>,
  output: Pick<FileOutput, 'id' | 'fileName'>,
  format: WorkbookFileFormat,
  acceptFailedValidation = false,
) {
  startDownloadInFrame(
    (await workbookOutputLink(editor, output, format, acceptFailedValidation))
      .url,
  );
}
